import React, { useMemo, useRef } from "react";
import DocumentActionBar from "./DocumentActionBar";
import DocumentListPanel from "./DocumentListPanel";
import DocumentPreview from "./DocumentPreview";
import DocumentPrintActions from "./print/DocumentPrintActions";
import DocumentStatusBanner from "./DocumentStatusBanner";
import DocumentSummary from "./DocumentSummary";
import DocumentTimeline from "./DocumentTimeline";
import {
  defaultDocumentConfigs,
  getDocumentNumber,
  getDocumentStatus,
  getPartyName,
} from "./documentHelpers";
import "./DocumentWorkspace.css";

const DocumentDetailLayout = ({
  rows = [],
  selectedRow,
  onSelect,
  onBack,
  config,
  onAction,
}) => {
  const resolvedConfig = useMemo(
    () => ({ ...(defaultDocumentConfigs[config?.documentType] || {}), ...config }),
    [config],
  );
  const row = selectedRow || rows[0] || {};
  const activityRef = useRef(null);
  const status = getDocumentStatus(row, resolvedConfig);
  const printActions = useMemo(
    () => (resolvedConfig.actions || []).filter((action) => action === "Print" || action === "Download PDF"),
    [resolvedConfig.actions],
  );
  const regularActions = useMemo(
    () => (resolvedConfig.actions || []).filter((action) => action !== "Print" && action !== "Download PDF"),
    [resolvedConfig.actions],
  );

  const handleAction = (action) => {
    if (onAction) onAction(action, row);
  };

  return (
    <section className="document-workspace">
      <DocumentListPanel
        rows={rows}
        selectedRow={row}
        config={resolvedConfig}
        onSelect={onSelect}
        onBack={onBack}
      />

      <main className="document-detail-panel">
        <div className="document-detail-top">
          <div>
            <div className="document-title-row">
              <h2>{getDocumentNumber(row, resolvedConfig)}</h2>
              <span className={`document-pill ${status.tone}`}>{status.label}</span>
            </div>
            <p>
              {resolvedConfig.singular || "Document"} for <strong>{getPartyName(row, resolvedConfig)}</strong>
            </p>
          </div>
          <div className="document-actions">
            <DocumentActionBar
              actions={regularActions}
              onAction={handleAction}
            />
            {printActions.length > 0 && (
              <DocumentPrintActions
                row={row}
                config={resolvedConfig}
                documentType={resolvedConfig.documentType}
                onError={(message) => window.alert(message)}
              />
            )}
          </div>
        </div>

        <DocumentStatusBanner
          row={row}
          config={resolvedConfig}
          onViewActivity={() => activityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />

        <div className="document-detail-grid">
          <DocumentPreview row={row} config={resolvedConfig} />
          <DocumentSummary row={row} config={resolvedConfig} />
          <div ref={activityRef}>
            <DocumentTimeline row={row} config={resolvedConfig} />
          </div>
        </div>
      </main>
    </section>
  );
};

export default DocumentDetailLayout;
